import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import type { Order, NewsItem } from "@mnp/shared";
import { api } from "../lib/api.js";
import { Icon, TgHeader, TabBar } from "../components/ui/index.js";

function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

function formatNewsDate(iso: string): string {
  const d = new Date(iso);
  const months = [
    "янв",
    "фев",
    "мар",
    "апр",
    "мая",
    "июн",
    "июл",
    "авг",
    "сен",
    "окт",
    "ноя",
    "дек",
  ];
  // BUG-07: use local getters (like the rest of the client UI, which formats via
  // toLocaleDateString). UTC getters showed the previous day for news published in
  // the 00:00–04:00 Tbilisi (UTC+4) window.
  return `${d.getDate()} ${months[d.getMonth()]}`;
}

const NEWS_INTERVAL_MS = 9000;

function NewsCarousel({ items }: { items: NewsItem[] }) {
  const [i, setI] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (items.length <= 1) {
      setProgress(100);
      return;
    }
    setProgress(0);
    const tick = 60;
    const id = setInterval(() => {
      setProgress((p) => {
        const next = p + (tick / NEWS_INTERVAL_MS) * 100;
        if (next >= 100) {
          setI((curr) => (curr + 1) % items.length);
          return 0;
        }
        return next;
      });
    }, tick);
    return () => clearInterval(id);
  }, [items.length]);

  if (!items.length) return null;
  const n = items[i] ?? items[0];
  if (!n) return null;

  return (
    <div className="pr-news-car" data-type={n.type}>
      <div className="pr-news-progress">
        <i style={{ width: progress + "%" }} />
      </div>
      <div className="pr-news-card-inner">
        <div className="pr-news-icon">
          <Icon name={n.type === "warning" ? "alertCircle" : "infoCircle"} size={14} />
        </div>
        <div className="pr-news-body">
          <p className="pr-news-title">{n.title}</p>
          <p className="pr-news-text">{n.body}</p>
          <p className="pr-news-date">{formatNewsDate(n.publishedAt)}</p>
        </div>
        <Icon name="chevronRight" size={14} color="var(--mnp-fg-3)" className="pr-news-chev" />
      </div>
      {items.length > 1 && (
        <div className="pr-news-dots">
          {items.map((_, k) => (
            <button
              key={k}
              type="button"
              data-active={k === i ? "true" : undefined}
              onClick={() => {
                setI(k);
                setProgress(0);
              }}
              aria-label={"Новость " + (k + 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type DirectionRowProps = {
  primary?: boolean;
  disabled?: boolean;
  icon: "send" | "package" | "box";
  title: string;
  sub: string;
  soon?: string;
  onClick?: () => void;
};

function DirectionRow({ primary, disabled, icon, title, sub, soon, onClick }: DirectionRowProps) {
  return (
    <button
      type="button"
      className={"pr-dir-row" + (primary ? " primary" : "")}
      data-disabled={disabled || undefined}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
    >
      <div className="pr-dir-icon">
        <Icon name={icon} size={22} color={primary ? "#fff" : "currentColor"} />
      </div>
      <div className="pr-dir-text">
        <p className="pr-dir-title">{title}</p>
        <p className="pr-dir-sub">{sub}</p>
      </div>
      {soon ? (
        <span className="pr-dir-soon">{soon}</span>
      ) : (
        <Icon name="chevronRight" size={18} color={primary ? "#fff" : "var(--mnp-fg-3)"} />
      )}
    </button>
  );
}

export function HomeScreen() {
  const nav = useNavigate();
  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => api<any>("/profile"),
  });
  const { data: orders } = useQuery({
    queryKey: ["orders", "active"],
    queryFn: () => api<Order[]>("/orders?status=active"),
  });
  const { data: history } = useQuery({
    queryKey: ["orders", "history"],
    queryFn: () => api<Order[]>("/orders?status=history"),
  });
  const { data: newsResp } = useQuery({
    queryKey: ["news"],
    queryFn: () => api<{ items: NewsItem[]; source: "db" | "fallback" }>("/news"),
    staleTime: 60_000,
  });

  const activeCount = orders?.length ?? 0;
  const historyCount = history?.length ?? 0;
  // "ready" — статус legacy/flex-строк; статус готовности архивного модуля
  // Ozon/WB больше не приходит (/orders отдаёт только flex с PR4).
  const readyOrder = orders?.find((o) => o.status === "ready");
  const firstName = profile?.fullName?.split(" ")[0] ?? "";
  const news = newsResp?.items ?? [];

  return (
    <div className="app-shell">
      <TgHeader title="Mark & Post" />
      <div className="screen pad" style={{ position: "relative", paddingBottom: 24 }}>
        {/* Greeting */}
        <div className="flex items-center gap-3 mb-4">
          <div className="avatar">{initials(profile?.fullName ?? "")}</div>
          <div className="grow">
            <p className="t-small" style={{ margin: 0 }}>
              Привет,
            </p>
            <p className="t-h3" style={{ margin: 0 }}>
              {firstName || "друг"}
            </p>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            style={{ padding: 8, borderRadius: "50%", width: 36, height: 36 }}
          >
            <Icon name="bell" size={18} />
          </button>
        </div>

        {/* Hero — посылка ждёт (если есть заказ к выдаче). CTA → профиль */}
        {readyOrder && (
          <button type="button" className="pr-hero" onClick={() => nav("/profile")}>
            <div className="pr-hero-icon">
              <Icon name="qr" size={22} />
            </div>
            <div className="pr-hero-text">
              <p className="pr-hero-eyebrow">Посылка ждёт вас!</p>
              <p className="pr-hero-title">Открыть мой QR →</p>
            </div>
          </button>
        )}

        {/* Карточка «Мои заказы» */}
        <Link to="/orders" className="pr-orders-card">
          <div className="pr-orders-icon">
            <Icon name="package" size={18} />
          </div>
          <div className="pr-orders-text">
            <p className="pr-orders-title">Мои заказы</p>
            <p className="pr-orders-sub">
              {activeCount} активных · {historyCount} в истории
            </p>
          </div>
          {activeCount > 0 && <span className="badge badge-brand">{activeCount}</span>}
          <Icon name="chevronRight" size={16} color="var(--mnp-fg-3)" />
        </Link>

        {/* Новости. Пока новостей нет — показываем спокойную заглушку
            «Новостей пока нет», а не прячем блок и не выдумываем демо-новости.
            Гейтим по newsResp, чтобы заглушка не мигала во время загрузки. */}
        {newsResp !== undefined && (
          <>
            <p className="pr-eyebrow">
              Новости
              {news.length > 1 && <span className="count">{news.length}</span>}
            </p>
            {news.length > 0 ? (
              <NewsCarousel items={news} />
            ) : (
              <div className="pr-news-empty">
                <div className="pr-news-icon">
                  <Icon name="infoCircle" size={14} />
                </div>
                <div className="pr-news-body">
                  <p className="pr-news-title">Новостей пока нет</p>
                  <p className="pr-news-text">
                    Здесь будут появляться важные объявления о доставке и новых направлениях.
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Направления — 3 широкие карточки */}
        <p className="pr-eyebrow">Направления</p>
        <div className="pr-dir-stack">
          <DirectionRow
            primary
            icon="send"
            title="Отправить посылку"
            sub="Гибкая форма · 44 страны"
            onClick={() => nav("/new-parcel")}
          />
          <DirectionRow
            icon="box"
            title="Посылка в СНГ"
            sub="Россия · Беларусь · Казахстан"
            onClick={() => nav("/new-parcel-cis")}
          />
          <DirectionRow
            icon="package"
            title="Упаковка"
            sub="Коробки, скотч, плёнка"
            onClick={() => nav("/shop")}
          />
          <DirectionRow
            disabled
            icon="box"
            title="Заказ из российских маркетплейсов"
            sub="Ozon, Wildberries и другие — скоро будет доступно"
            soon="В разработке"
          />
        </div>
      </div>
      <TabBar />
    </div>
  );
}
